import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { useLocation, useNationalSiteContext } from 'app/common/hooks';
import { get, isEmpty } from 'lodash';

/**
 * Generates a QR code data URL for the given URL.
 *
 * This hook allows the pdf templates to get the QR image before the PDF starts rendering.
 *
 * @returns {{qrUrl: string, qrImage: string, error: unknown}}
 */
export function useEFlyerQRCode() {
  const [qrImage, setQrImage] = useState(null);
  const [error, setError] = useState(null);

  const location = useLocation();
  const natlSiteContext = useNationalSiteContext();
  const { currentApplication } = natlSiteContext;
  const isEflyerQrCodeEnabled =
    get(currentApplication, 'attributes.eflyerQrCodeEnabled') !== false;
  const isDealerWhiteLabel =
    natlSiteContext.isWhiteLabel &&
    !natlSiteContext.nationalSiteOrDealerNetworkContext;

  // if eflyerQrCodeEnabled is false, no QrUrl, no display url
  // if single dealer white label, qrurl does not have dealerLocation
  // if dealer network, qrUrl has dealerLocation
  // if branded national site, qrUrl has dealerLocation

  // displayUrl never has dealerLocation

  const displayUrl = useMemo(() => {
    if (!isEflyerQrCodeEnabled) {
      return null;
    }
    const pathname = location.pathname.replace('/pdf', '');
    return `https://${window.location.host}${pathname}`;
  }, [location.pathname, isEflyerQrCodeEnabled]);

  const qrUrl = useMemo(() => {
    if (!isEflyerQrCodeEnabled || !displayUrl) {
      return null;
    }

    if (isDealerWhiteLabel || isEmpty(currentApplication)) {
      return displayUrl;
    }

    const identifierValue = get(currentApplication, 'identifierValue');
    const dealerLocationParam = `?dealerLocation=${identifierValue}`;

    return `${displayUrl}${dealerLocationParam}`;
  }, [
    displayUrl,
    isEflyerQrCodeEnabled,
    isDealerWhiteLabel,
    currentApplication
  ]);

  useEffect(() => {
    let cancelled = false;
    const options = {
      size: 96,
      bgColor: '#FFFFFF',
      fgColor: '#000000',
      level: 'L'
    };

    async function generateQR() {
      if (!qrUrl) {
        setQrImage(null);
        setError(null);
        return;
      }
      try {
        const dataUrl = await QRCode.toDataURL(qrUrl, options);
        if (!cancelled) {
          setQrImage(dataUrl);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setError(e);
          setQrImage(null);
        }
      }
    }

    generateQR();
    return () => {
      cancelled = true;
    };
  }, [qrUrl]);

  return { qrUrl: displayUrl, qrImage, error };
}
